// Wix SDK — browser-side client (OAuth Visitor authed).
// Used in Client Components for cart + checkout redirect.
//
// Lazy-loaded via dynamic import() so the ~128KB SDK transfer only lands on
// routes that actually use the cart (PDP, cart page, header cart count), not
// the homepage. NEVER import from a server component — use lib/wix.ts there.

'use client';

import { cookieTokenStorage } from './auth-cookies';

const clientId = process.env.NEXT_PUBLIC_WIX_CLIENT_ID;

if (!clientId) {
  throw new Error(
    '[wix-browser] Missing NEXT_PUBLIC_WIX_CLIENT_ID. Generate at manage.wix.com → Settings → Headless → OAuth Apps.',
  );
}

async function loadClient() {
  const [sdk, ecom, redirectsMod] = await Promise.all([
    import('@wix/sdk'),
    import('@wix/ecom'),
    import('@wix/redirects'),
  ]);
  return sdk.createClient({
    modules: {
      currentCart: ecom.currentCart,
      redirects: redirectsMod.redirects,
    },
    auth: sdk.OAuthStrategy({ clientId: clientId!, tokenStorage: cookieTokenStorage }),
  });
}

export type WixBrowserClient = Awaited<ReturnType<typeof loadClient>>;

let cached: WixBrowserClient | null = null;
let inflight: Promise<WixBrowserClient> | null = null;

/** Memoized browser-side Wix client; SDK chunk fetched once on first call. */
export async function getWixBrowser(): Promise<WixBrowserClient> {
  if (cached) return cached;
  if (inflight) return inflight;
  // Clear `inflight` on REJECTION as well as success. Without the catch arm a
  // failed first init (offline, blocked CDN chunk, SDK import error) is
  // memoised for the life of the page: every later getWixBrowser() returns the
  // same rejected promise, so a shopper pressing "try again" can never get a
  // working client back.
  inflight = loadClient().then(
    (client) => {
      cached = client;
      inflight = null;
      return client;
    },
    (err) => {
      inflight = null;
      throw err;
    },
  );
  return inflight;
}
